// staffCapacityEngine.js - Pure staff-count / gender-capacity business logic.
// Deliberate mirror of UlloorStyles@e7a5f9b:src/utils/staffCapacityEngine.js (the mobile app's
// capacity engine) so web and mobile compute identical answers from the same salon profile
// fields. Web only uses the config-relevant subset (read-only display/preview) — canBookSlot is
// ported unused-for-now purely to keep this file a complete mirror for future drift-checking;
// nothing on web calls it.

/**
 * Get day name from date object
 * @param {Date} date - Date object
 * @returns {string} - Day name (e.g., 'Monday', 'Tuesday', etc.)
 */
export const getDayNameFromDate = (date) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
};

/**
 * Get staff count for a specific date based on salon's staff count configuration
 * For unisex salons, can optionally specify gender type to get men/women staff count
 *
 * BACKWARD COMPATIBILITY:
 * - Old clients (without update) call this without genderType parameter
 * - They will use the regular staffCount which is set to MIN(menCount, womenCount)
 * - This prevents overbooking while old clients catch up with updates
 *
 * @param {Object} salon - Salon object with staffCountData
 * @param {Date} date - The date to get staff count for
 * @param {string} genderType - Optional: 'men' or 'women' for unisex salons
 * @returns {number} - Staff count for that day
 */
export const getStaffCountForDate = (salon, date, genderType = null) => {
  try {
    // Handle different data structures (transformed salon vs raw document)
    const profile = salon?.userData?.profile || salon?.profile || salon;
    const staffCountData = profile?.staffCountData || salon?.staffCountData;

    // Check multiple locations for servicesFor field
    const servicesFor = salon?.servicesFor ||
                        salon?.userData?.profile?.servicesFor ||
                        profile?.servicesFor ||
                        'Unisex';

    // For unisex salons with gender-based staff counts
    if (servicesFor === 'Unisex' && genderType) {
      // Check multiple locations for genderStaffCount
      const genderStaffData = profile?.genderStaffCount ||
                              salon?.userData?.profile?.genderStaffCount ||
                              salon?.genderStaffCount;

      if (genderStaffData) {
        // Check if there's day-based configuration for this specific gender
        // Note: Gender day-wise is independent of main staffCountData
        if (genderStaffData[genderType]?.byDay && Object.keys(genderStaffData[genderType].byDay).length > 0) {
          const dayOfWeek = getDayNameFromDate(date);
          const dayCounts = genderStaffData[genderType].byDay;
          // Use ?? to preserve 0 values (staff unavailable)
          return dayCounts[dayOfWeek] ?? genderStaffData[genderType]?.count ?? 1;
        }

        // Return the gender-specific count (use ?? to preserve 0 values)
        return genderStaffData[genderType]?.count ??
               (genderType === 'men' ? (profile?.staffCount ?? 1) : 1);
      }

      // Fallback for unisex without genderStaffCount:
      // men uses existing staffCount, women defaults to 1
      if (genderType === 'men') {
        // Use existing staff count logic for men
        if (!staffCountData || staffCountData.type === 'all_days') {
          return staffCountData?.count ?? profile?.staffCount ?? 1;
        }
        if (staffCountData.type === 'by_day' && staffCountData.counts) {
          const dayOfWeek = getDayNameFromDate(date);
          return staffCountData.counts[dayOfWeek] ?? 1;
        }
        return profile?.staffCount ?? 1;
      } else {
        // Women defaults to 1 if not configured
        return 1;
      }
    }

    // Non-unisex salons or no gender specified - use regular staff count logic
    if (!staffCountData || staffCountData.type === 'all_days') {
      return staffCountData?.count ?? profile?.staffCount ?? 1;
    }

    // If it's configured by day, get the day of the week and return the specific count
    if (staffCountData.type === 'by_day' && staffCountData.counts) {
      const dayOfWeek = getDayNameFromDate(date);
      return staffCountData.counts[dayOfWeek] ?? 1;
    }

    // Fallback to default staff count
    return profile?.staffCount ?? 1;
  } catch (error) {
    return salon?.staffCount ?? salon?.userData?.profile?.staffCount ?? 1; // Fallback
  }
};

/**
 * Get AVAILABLE staff count for a date (base count minus leaves)
 * @param {Object} salon - Salon data
 * @param {Date} date - The date to check
 * @param {string} genderType - 'men', 'women', or null
 * @param {Object} leaveData - Pre-fetched leave data for the date (optional)
 * @returns {number} - Available staff count for that day
 */
export const getAvailableStaffCountForDate = (salon, date, genderType = null, leaveData = null) => {
  const profile = salon?.userData?.profile || salon?.profile || salon;
  const servicesFor = salon?.servicesFor ||
                      salon?.userData?.profile?.servicesFor ||
                      salon?.profile?.servicesFor ||
                      'Unisex';

  // Precise path: a named roster on a single-gender salon — exact staff identity means we can
  // apply the day-of-week schedule and that date's leave by staff id instead of a flat count.
  // (Unisex salons always go through getStaffCapability instead, which already does this.)
  if (servicesFor !== 'Unisex' && Array.isArray(profile?.staffList)) {
    const dayName = getDayNameFromDate(date);
    const activeList = activeStaffForDay(profile.staffList, profile?.staffCountData?.staffSchedule, dayName);

    if (Array.isArray(leaveData?.staffIds)) {
      const onLeave = new Set(leaveData.staffIds);
      return Math.max(0, activeList.filter((s) => !onLeave.has(s.id)).length);
    }

    // Legacy aggregate leave count, applied on top of the (possibly schedule-filtered) roster
    const leaveCount = leaveData?.leaveCount ?? 0;
    return Math.max(0, activeList.length - leaveCount);
  }

  // Get base staff count using existing function
  const baseCount = getStaffCountForDate(salon, date, genderType);

  if (!leaveData) {
    // No leave data provided, return base count
    return baseCount;
  }

  // Calculate leave count for this gender type
  let leaveCount = 0;

  // Determine which leave count field to use based on salon type and gender
  if (servicesFor === 'Unisex' && genderType) {
    // For unisex salons with gender-specific staff, use gender-specific leave count
    if (genderType === 'men') {
      leaveCount = leaveData.menLeaveCount ?? 0;
    } else if (genderType === 'women') {
      leaveCount = leaveData.womenLeaveCount ?? 0;
    } else {
      // Fallback for unexpected gender type values - use general leaveCount
      leaveCount = leaveData.leaveCount ?? 0;
    }
  } else if (servicesFor === 'Men' || servicesFor === 'Women') {
    // For gender-specific salons (Men only or Women only), use general leaveCount
    leaveCount = leaveData.leaveCount ?? 0;
  } else {
    // Fallback: Non-unisex or no gender specified - use general leaveCount
    leaveCount = leaveData.leaveCount ?? 0;
  }

  // Return available count (never less than 0)
  return Math.max(0, baseCount - leaveCount);
};

/**
 * Filter a named-staff list down to whoever is actually scheduled on a given day of week.
 * `daySchedule` is a { Monday: [staffId, ...], ... } map — when a day isn't present in it, that
 * day hasn't been customized and everyone in the list is assumed to work it (matches how the old
 * numeric by-day counts defaulted an unset day to the base count). An explicit empty array for a
 * day means "nobody scheduled" (the owner marked that day off for this roster).
 * Exported (unlike mobile's private version) since the web day-schedule UI needs it directly.
 * @param {Array} list - menStaffList / womenStaffList / staffList
 * @param {Object|undefined} daySchedule
 * @param {string} dayName - result of getDayNameFromDate
 * @returns {Array} the subset of `list` active on that day
 */
export const activeStaffForDay = (list, daySchedule, dayName) => {
  if (!Array.isArray(list)) return [];
  if (daySchedule && Array.isArray(daySchedule[dayName])) {
    const activeIds = new Set(daySchedule[dayName]);
    return list.filter((s) => activeIds.has(s.id));
  }
  return list;
};

/**
 * Resolve a unisex salon's { menOnly, womenOnly, both } headcounts for a date.
 * - If the owner has named staff (menStaffList/womenStaffList), each entry's own
 *   `serviceFor` ('men'|'women'|'both') decides its bucket, regardless of which list it's
 *   in — the lists just control how many rows are shown under each heading in the UI. Each
 *   list's own day-of-week schedule (genderStaffCount.men/women.daySchedule) narrows it down
 *   to who's actually working that day, and an exact per-date leave list (leaveData.staffIds)
 *   excludes specific people on top of that — both precisely, by staff id.
 * - Otherwise (legacy salons that haven't named staff yet), fall back to the independent
 *   genderStaffCount numbers via getStaffCountForDate (still honors any by-day config), with
 *   `both` implicitly 0 — i.e. identical to today's behavior. `preciseLeaveApplied` tells the
 *   caller whether leave was already resolved by exact staff id (so it shouldn't also apply the
 *   old aggregate-count leave math on top and double-subtract).
 */
const getUnisexRosterCapability = (profile, date, leaveData) => {
  const menStaffList = profile?.menStaffList;
  const womenStaffList = profile?.womenStaffList;

  if (Array.isArray(menStaffList) || Array.isArray(womenStaffList)) {
    const dayName = getDayNameFromDate(date);
    const activeMen = activeStaffForDay(menStaffList, profile?.genderStaffCount?.men?.daySchedule, dayName);
    const activeWomen = activeStaffForDay(womenStaffList, profile?.genderStaffCount?.women?.daySchedule, dayName);

    const preciseLeaveApplied = Array.isArray(leaveData?.staffIds);
    const onLeave = preciseLeaveApplied ? new Set(leaveData.staffIds) : null;
    const roster = onLeave
      ? [...activeMen, ...activeWomen].filter((s) => !onLeave.has(s.id))
      : [...activeMen, ...activeWomen];

    let menOnly = 0;
    let womenOnly = 0;
    let both = 0;
    for (const staff of roster) {
      if (staff?.serviceFor === 'both') both += 1;
      else if (staff?.serviceFor === 'women') womenOnly += 1;
      else menOnly += 1; // default / 'men'
    }
    return { menOnly, womenOnly, both, preciseLeaveApplied };
  }

  // Legacy fallback: independent counts, no shared "both" pool
  const menOnly = getStaffCountForDate({ profile }, date, 'men');
  const womenOnly = getStaffCountForDate({ profile }, date, 'women');
  return { menOnly, womenOnly, both: 0, preciseLeaveApplied: false };
};

/**
 * Get the available (leave-adjusted) staff capability for a unisex salon on a date.
 * @param {Object} salon - Salon object (raw doc or transformed)
 * @param {Date} date - The date to check
 * @param {Object|null} leaveData - Pre-fetched leave doc for the date ({ menLeaveCount, womenLeaveCount, staffIds? })
 * @returns {{ menOnly: number, womenOnly: number, both: number }}
 */
export const getStaffCapability = (salon, date, leaveData = null) => {
  const profile = salon?.userData?.profile || salon?.profile || salon;
  const { menOnly, womenOnly, both, preciseLeaveApplied } = getUnisexRosterCapability(profile, date, leaveData);

  if (!leaveData || preciseLeaveApplied) {
    // Either no leave recorded for this date, or leave was already resolved precisely by exact
    // staff id above — applying the aggregate math below on top would double-subtract.
    return { menOnly, womenOnly, both };
  }

  const menLeaveCount = leaveData.menLeaveCount ?? 0;
  const womenLeaveCount = leaveData.womenLeaveCount ?? 0;

  const availableMenOnly = Math.max(0, menOnly - menLeaveCount);
  const menLeaveSpillover = Math.max(0, menLeaveCount - menOnly);
  const availableWomenOnly = Math.max(0, womenOnly - womenLeaveCount);
  const womenLeaveSpillover = Math.max(0, womenLeaveCount - womenOnly);

  // A "both" staff member on leave reduces capacity for both genders at once. Two aggregate
  // leave counts (not tied to a specific staff member) can't tell us whether they're reporting
  // the same missing "both" person twice, so we take the larger spillover rather than summing —
  // this stays conservative and never over-reports available capacity.
  const bothLeaveReduction = Math.max(menLeaveSpillover, womenLeaveSpillover);
  const availableBoth = Math.max(0, both - bothLeaveReduction);

  return { menOnly: availableMenOnly, womenOnly: availableWomenOnly, both: availableBoth };
};

/**
 * Feasibility check for adding one more booking of `demandGender` to a time slot, given a
 * unisex salon's current staff capability and the bookings already confirmed for that slot.
 * Not used anywhere on web today (web has no slot/booking-capacity logic) — ported only so this
 * file stays a complete mirror of the mobile capacity engine for future drift-checking.
 * @param {{ menOnly: number, womenOnly: number, both: number }} capability
 * @param {number} menBooked - existing confirmed bookings needing a men-capable staff member
 * @param {number} womenBooked - existing confirmed bookings needing a women-capable staff member
 * @param {'men'|'women'} demandGender - gender bucket the new booking would add to
 * @returns {boolean}
 */
export const canBookSlot = (capability, menBooked, womenBooked, demandGender) => {
  const { menOnly = 0, womenOnly = 0, both = 0 } = capability || {};
  const nextMenBooked = menBooked + (demandGender === 'men' ? 1 : 0);
  const nextWomenBooked = womenBooked + (demandGender === 'women' ? 1 : 0);

  return (
    nextMenBooked <= menOnly + both &&
    nextWomenBooked <= womenOnly + both &&
    nextMenBooked + nextWomenBooked <= menOnly + womenOnly + both
  );
};
